'use client';

import { useState, useCallback, useEffect } from 'react';

import { Sheet, SheetContent } from '@/components/ui/sheet';
import { GradientSpinner } from '@/components/ui/GradientSpinner';
import { EXTERNAL_URLS } from '@/shared/constants/externalLinks';

interface HelpCenterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional article path to load directly (e.g., '/articles/123-rep-score') */
  articlePath?: string;
}

/**
 * Help Center Sheet - In-app bottom sheet for Help Center content
 *
 * Replaces external links to support.motito.co with an in-app experience.
 * This solves the WebView "trapped user" problem where external links
 * open inside the MoMo WebView with no way back.
 *
 * Features:
 * - Bottom sheet with rounded corners (signals "layer, not navigation")
 * - GradientSpinner loading state while iframe loads
 * - 3 exit routes: X button, swipe down, tap backdrop
 * - No duplicate header (iframe provides Help Center's native UI)
 * - Timeout-based fallback if iframe is blocked by X-Frame-Options
 */
function HelpCenterSheet({
  open,
  onOpenChange,
  articlePath,
}: HelpCenterSheetProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  // Construct the full URL
  const helpCenterUrl = articlePath
    ? `${EXTERNAL_URLS.HELP_CENTER}${articlePath}`
    : EXTERNAL_URLS.HELP_CENTER;

  // Reset loading state when sheet opens
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (newOpen) {
      setIsLoading(true);
      setHasError(false);
      setShowFallback(false);
    }
    onOpenChange(newOpen);
  }, [onOpenChange]);

  // Handle iframe load completion
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Handle iframe error
  const handleIframeError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  // Timeout-based detection for X-Frame-Options blocking
  // onError doesn't fire for X-Frame-Options blocks, so we detect via timeout
  useEffect(() => {
    if (!open) return;

    const timeout = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        setShowFallback(true);
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [open, isLoading]);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[95vh] rounded-t-3xl p-0 flex flex-col"
      >
        {/* Drag handle indicator */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="h-1 w-10 rounded-full bg-gray-300" />
        </div>

        {/* Content area */}
        <div className="relative flex-1 overflow-hidden">
          {/* Loading state */}
          {isLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
              <GradientSpinner size={48} />
              <p className="mt-4 text-sm text-gray-500">Loading Help Center...</p>
            </div>
          )}

          {/* Error state */}
          {hasError && !isLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white px-6">
              <p className="text-center text-gray-600">
                Unable to load Help Center.
              </p>
              <button
                type="button"
                onClick={() => {
                  setIsLoading(true);
                  setHasError(false);
                }}
                className="mt-4 text-sm font-medium text-motitoBlue hover:underline"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="mt-2 text-sm text-gray-500 hover:underline"
              >
                Close
              </button>
            </div>
          )}

          {/* Fallback state - iframe blocked by X-Frame-Options */}
          {showFallback && !isLoading && !hasError && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white px-6">
              <p className="text-center text-gray-600 mb-4">
                Unable to load Help Center in-app.
              </p>
              <a
                href={helpCenterUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-motitoBlue underline font-medium"
              >
                Open Help Center in browser
              </a>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="mt-4 text-sm text-gray-500 hover:underline"
              >
                Close
              </button>
            </div>
          )}

          {/* Iframe - always render but hidden during loading */}
          <iframe
            src={helpCenterUrl}
            title="Help Center"
            className="h-full w-full border-0"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

HelpCenterSheet.displayName = 'HelpCenterSheet';

export default HelpCenterSheet;
